import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  analyzeQuestionBulkUpload,
  applyQuestionBulkUpload,
} from "../api/adminClient";
import { parseSpreadsheetFile } from "../utils/parseSpreadsheetFile";
import { isAbortError } from "../utils/fetchAbort";
import { downloadQuestionBulkTemplateCsv } from "./questionBulkTemplate";

const PILLAR_OPTIONS = [
  "Strategic clarity",
  "Scalable talent",
  "Relentless focus",
  "Disciplined execution",
  "Energized culture",
  "Financial performance",
];

const TYPE_OPTIONS = [
  { value: "rating", label: "Star rating (1–5)" },
  { value: "importance_agreement", label: "Importance × Agreement" },
  { value: "open_ended", label: "Open ended" },
  { value: "dropdown", label: "Dropdown" },
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
];

const FIELD_ALIASES = {
  pillar: ["pillar", "pillar name", "pillar_name", "section", "section name", "section_name", "dimension", "category"],
  question_text: ["question text", "question_text", "question", "text", "wording"],
  question_type: ["question type", "question_type", "type", "qtype"],
  question_id: ["question id", "question_id", "id", "qid"],
  options: ["options", "choices", "answer options", "answer_options"],
  allow_multiple: ["allow multiple", "allow_multiple", "multiple", "multi"],
  include_in_lite: ["include in lite", "include_in_lite", "lite", "in lite", "lite question"],
};

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function statusStyles(status) {
  if (status === "match") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (status === "unmatch") return "bg-red-50 text-red-800 border-red-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function hintStyles(severity) {
  if (severity === "error") return "bg-red-50 text-red-800 border-red-200";
  if (severity === "warning") return "bg-amber-50 text-amber-900 border-amber-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function rowStatusLabel(status) {
  if (status === "matched") return "Matched";
  if (status === "possible_match") return "Possible match";
  if (status === "new") return "New";
  if (status === "invalid") return "Unmatched";
  if (status === "skipped") return "Skipped";
  return status || "—";
}

function rowStatusClass(status) {
  if (status === "matched") return "text-emerald-800";
  if (status === "possible_match") return "text-amber-800";
  if (status === "new") return "text-sky-800";
  if (status === "invalid") return "text-red-700";
  if (status === "skipped") return "text-amber-800";
  return "text-slate-600";
}

function normKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function yesNo(value) {
  if (value === true || value === 1) return "Y";
  if (value === false || value === 0) return "N";
  const key = normKey(value);
  if (["y", "yes", "true", "1"].includes(key)) return "Y";
  if (["n", "no", "false", "0"].includes(key)) return "N";
  return String(value ?? "").trim();
}

function optionsToText(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join("|");
  return String(value ?? "").trim();
}

function pickField(raw, canonical) {
  if (!raw || typeof raw !== "object") return "";
  if (raw[canonical] != null && String(raw[canonical]).trim() !== "") return raw[canonical];
  const aliases = FIELD_ALIASES[canonical] || [canonical];
  for (const [key, value] of Object.entries(raw)) {
    if (aliases.includes(normKey(key))) return value;
  }
  return raw[canonical] ?? "";
}

function toCanonicalRow(raw) {
  return {
    pillar: String(pickField(raw, "pillar") ?? "").trim(),
    question_text: String(pickField(raw, "question_text") ?? "").trim(),
    question_type: String(pickField(raw, "question_type") ?? "").trim(),
    options: optionsToText(pickField(raw, "options")),
    allow_multiple: yesNo(pickField(raw, "allow_multiple")),
    include_in_lite: yesNo(pickField(raw, "include_in_lite")),
    question_id: String(pickField(raw, "question_id") ?? "").trim(),
  };
}

function needsOptions(type) {
  const key = normKey(type);
  return ["dropdown", "single choice", "single_choice", "multiple choice", "multiple_choice"].includes(key);
}

function EditablePreviewTable({ rows, busy, onPatch }) {
  if (!rows.length) {
    return (
      <p className="mt-3 text-sm text-slate-400">No question rows to edit yet.</p>
    );
  }
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
        Preview &amp; edit ({rows.length})
      </p>
      <p className="text-xs text-slate-500 mb-2">
        Edit unmatched or matched rows here. Mapping re-checks after you change a field.
      </p>
      <div className="max-h-80 overflow-auto border rounded-lg text-xs">
        <table className="w-full min-w-[720px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left w-12">Row</th>
              <th className="p-2 text-left w-40">Pillar</th>
              <th className="p-2 text-left">Question</th>
              <th className="p-2 text-left w-36">Type</th>
              <th className="p-2 text-left w-44">Options</th>
              <th className="p-2 text-left w-28">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const typeValue = r.question_type || "";
              const typeKnown = TYPE_OPTIONS.some((t) => t.value === typeValue);
              return (
                <tr key={r.row} className="border-t align-top">
                  <td className="p-2 text-slate-500">{r.row}</td>
                  <td className="p-2">
                    <select
                      value={PILLAR_OPTIONS.includes(r.pillar) ? r.pillar : r.pillar || ""}
                      disabled={busy}
                      onChange={(e) => onPatch(r.row, { pillar: e.target.value })}
                      className="w-full border border-slate-200 rounded-md px-1.5 py-1 bg-white"
                    >
                      {!PILLAR_OPTIONS.includes(r.pillar) ? (
                        <option value={r.pillar || ""}>{r.pillar || "Select pillar"}</option>
                      ) : null}
                      {PILLAR_OPTIONS.map((title) => (
                        <option key={title} value={title}>
                          {title}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <textarea
                      value={r.question_text || ""}
                      disabled={busy}
                      rows={2}
                      onChange={(e) => onPatch(r.row, { question_text: e.target.value })}
                      className="w-full border border-slate-200 rounded-md px-1.5 py-1 resize-y min-h-[2.5rem]"
                    />
                    {r.hint ? <p className="mt-1 text-amber-800">{r.hint}</p> : null}
                    {r.changes?.length ? (
                      <p className="mt-1 text-slate-500">Changes: {r.changes.join(", ")}</p>
                    ) : null}
                  </td>
                  <td className="p-2">
                    <select
                      value={typeKnown ? typeValue : typeValue}
                      disabled={busy}
                      onChange={(e) => onPatch(r.row, { question_type: e.target.value })}
                      className="w-full border border-slate-200 rounded-md px-1.5 py-1 bg-white"
                    >
                      {!typeKnown ? (
                        <option value={typeValue}>{typeValue || "Select type"}</option>
                      ) : null}
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    {needsOptions(typeValue) ? (
                      <input
                        type="text"
                        value={optionsToText(r.options)}
                        disabled={busy}
                        placeholder="A|B|C"
                        onChange={(e) => onPatch(r.row, { options: e.target.value })}
                        className="w-full border border-slate-200 rounded-md px-1.5 py-1"
                      />
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className={`p-2 whitespace-nowrap font-medium ${rowStatusClass(r.status)}`}>
                    {rowStatusLabel(r.status)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BulkQuestionUploadModal({
  open,
  surveyId,
  templateName = "",
  organizationId = "",
  onClose,
  onApplied,
  overlayClassName = "fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4",
}) {
  const [previewFile, setPreviewFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [newName, setNewName] = useState("");
  const [destination, setDestination] = useState("create");
  const [updateMode, setUpdateMode] = useState("merge");
  const analyzeTimer = useRef(null);
  const analyzeAbort = useRef(null);
  const surveyIdRef = useRef(surveyId);

  useEffect(() => {
    surveyIdRef.current = surveyId;
  }, [surveyId]);

  useEffect(() => {
    if (!open) return;
    setPreviewFile(null);
    setRows([]);
    setAnalysis(null);
    setBusy(false);
    setRechecking(false);
    setNewName("");
    setDestination(surveyId ? "update" : "create");
    setUpdateMode("merge");
    if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
    analyzeAbort.current?.abort();
  }, [open, surveyId]);

  useEffect(
    () => () => {
      if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
      analyzeAbort.current?.abort();
    },
    []
  );

  const summary = analysis?.summary || {};
  const hints = analysis?.hints || [];
  const previewRows = useMemo(() => {
    const analyzed = [...(analysis?.rows || [])].sort((a, b) => (a.row || 0) - (b.row || 0));
    if (analyzed.length) return analyzed;
    return rows.map((row, i) => ({
      row: i + 2,
      status: "invalid",
      hint: null,
      changes: [],
      ...row,
    }));
  }, [analysis, rows]);
  const missingRows = analysis?.missing_from_file || [];
  const analyzing = busy && !analysis;
  const matchedOk =
    Boolean(analysis?.can_apply) && analysis?.template_status === "match" && !busy && !rechecking;
  const canSubmit =
    destination === "create"
      ? matchedOk && !!newName.trim()
      : matchedOk && !!surveyId;

  const runAnalyze = async (nextRows, { fromFile = false } = {}) => {
    analyzeAbort.current?.abort();
    const controller = new AbortController();
    analyzeAbort.current = controller;
    if (fromFile) setBusy(true);
    else setRechecking(true);
    try {
      const res = await analyzeQuestionBulkUpload(
        { survey_id: surveyIdRef.current, rows: nextRows },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      const data = res.data || null;
      setAnalysis(data);
      if (fromFile) {
        if (data?.template_status === "match") {
          toast.success("Template matched. Edit rows below if needed, then choose how to apply.");
        } else {
          toast.error("Template did not match. Edit the rows below or fix the file.");
        }
      }
    } catch (e) {
      if (isAbortError(e) || controller.signal.aborted) return;
      if (fromFile) {
        toast.error(e.message || "Could not read or analyze the file");
        setRows([]);
        setAnalysis(null);
      } else {
        toast.error(e.message || "Could not re-check the edited rows");
      }
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
        setRechecking(false);
      }
    }
  };

  const parseAndAnalyze = async (file) => {
    setAnalysis(null);
    setBusy(true);
    try {
      const parsed = await parseSpreadsheetFile(file);
      if (!parsed.length) {
        setRows([]);
        toast.error("The file has no question rows.");
        setAnalysis({
          template_status: "unmatch",
          hints: [
            {
              severity: "error",
              message: "The file is empty. Download the template and add one row per question.",
            },
          ],
          summary: {
            uploaded: 0,
            matched: 0,
            new: 0,
            invalid: 0,
            skipped: 0,
            missing_from_file: 0,
            changed: 0,
          },
          rows: [],
          missing_from_file: [],
          can_apply: false,
        });
        setBusy(false);
        return;
      }
      const canonical = parsed.map(toCanonicalRow);
      setRows(canonical);
      const fromFile = String(file?.name || "")
        .replace(/\.(csv|xlsx|xls)$/i, "")
        .replace(/[-_]+/g, " ")
        .trim();
      setNewName((prev) => prev || fromFile || "");
      await runAnalyze(canonical, { fromFile: true });
    } catch (e) {
      toast.error(e.message || "Could not read or analyze the file");
      setRows([]);
      setAnalysis(null);
      setBusy(false);
    }
  };

  const patchPreviewRow = (excelRow, fields) => {
    const idx = excelRow - 2;
    if (idx < 0) return;
    setRows((prev) => {
      const nextRows = prev.map((row, i) => (i === idx ? { ...row, ...fields } : row));
      if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
      analyzeTimer.current = setTimeout(() => {
        runAnalyze(nextRows);
      }, 550);
      return nextRows;
    });
    setAnalysis((prev) => {
      if (!prev?.rows) return prev;
      return {
        ...prev,
        rows: prev.rows.map((r) =>
          r.row === excelRow
            ? {
                ...r,
                ...fields,
                options: fields.options != null ? fields.options : r.options,
              }
            : r
        ),
      };
    });
  };

  const handleDownload = () => {
    downloadQuestionBulkTemplateCsv();
  };

  const handleApply = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload =
        destination === "create"
          ? { mode: "create", name: newName.trim(), survey_id: surveyId || "", rows, ...(organizationId ? { organization_id: organizationId } : {}) }
          : { mode: updateMode, survey_id: surveyId, rows };
      const res = await applyQuestionBulkUpload(payload);
      toast.success(
        res.message ||
          (destination === "create" ? "Template created" : "Template updated")
      );
      onApplied?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Could not apply the upload");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const applyLabel =
    destination === "create"
      ? busy
        ? "Creating…"
        : "Create template"
      : busy
        ? "Updating…"
        : updateMode === "replace"
          ? "Replace template"
          : "Update template";

  return createPortal(
    <div className={overlayClassName}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bulk upload questions</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Upload a file, edit unmatched or matched rows in the preview, then update the selected template or create a new one.
              {templateName ? ` Selected template: "${templateName}".` : ""}
            </p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-2"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
            {previewFile ? (
              <p className="text-sm text-green-700">{previewFile.name}</p>
            ) : (
              <p className="text-sm text-gray-600 px-4 text-center">
                <span className="font-medium">Click to upload</span> CSV or Excel (.xlsx)
              </p>
            )}
            <input
              type="file"
              className="hidden"
              accept=".xlsx,.csv"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setPreviewFile({ name: f.name });
                parseAndAnalyze(f);
                e.target.value = "";
              }}
            />
          </label>

          <div className="flex flex-wrap justify-between gap-2 mt-2 text-sm text-gray-500">
            <span>
              Columns: Pillar, Question Text, Question Type. Optional: Options, Allow Multiple, Include in LITE.
            </span>
            <button
              type="button"
              className="text-[#004f73] hover:underline font-medium disabled:opacity-50"
              onClick={handleDownload}
            >
              Download template
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Pillars: Strategic clarity, Scalable talent, Relentless focus, Disciplined execution, Energized culture, Financial performance.
            Types: rating, open_ended, dropdown, single_choice, multiple_choice.
          </p>

          {analyzing ? (
            <p className="mt-4 text-sm text-[#004F73] flex items-center gap-2">
              <Spinner className="h-4 w-4" /> Analyzing file…
            </p>
          ) : null}

          {analysis ? (
            <div className="mt-4 space-y-3">
              <div className={`rounded-lg border px-3 py-2 text-sm font-semibold ${statusStyles(analysis.template_status)}`}>
                {analysis.template_status === "match"
                  ? "Template match — edit any row below, then choose whether to update the selected template or create a new one."
                  : "Template unmatched — edit the rows below to fix pillar, text, type, or options."}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">{summary.uploaded || 0} uploaded</span>
                <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">{summary.matched || 0} matched</span>
                <span className="px-2 py-1 rounded-full bg-sky-50 text-sky-800">{summary.new || 0} new</span>
                <span className="px-2 py-1 rounded-full bg-red-50 text-red-800">{summary.invalid || 0} unmatched</span>
                <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-800">
                  {summary.skipped || 0} skipped
                </span>
                <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-800">
                  {summary.missing_from_file || 0} missing from file
                </span>
                {rechecking ? (
                  <span className="px-2 py-1 rounded-full bg-[#004F73]/10 text-[#004F73] flex items-center gap-1">
                    <Spinner className="h-3 w-3" /> Re-checking…
                  </span>
                ) : null}
              </div>

              {hints.length ? (
                <div className="space-y-1.5">
                  {hints.map((h, i) => (
                    <p key={`${h.message}-${i}`} className={`text-sm rounded-lg border px-3 py-2 ${hintStyles(h.severity)}`}>
                      {h.row ? `Row ${h.row}: ` : ""}
                      {h.message}
                    </p>
                  ))}
                </div>
              ) : null}

              <EditablePreviewTable
                rows={previewRows}
                busy={busy}
                onPatch={patchPreviewRow}
              />

              {analysis.template_status === "match" ? (
                <div className="rounded-lg border border-[#004F73]/20 bg-[#004F73]/5 p-3 space-y-3">
                  <p className="text-sm font-semibold text-[#004F73]">Destination</p>
                  <div className="flex flex-col gap-2 text-sm">
                    {surveyId ? (
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="bulk-destination"
                          className="mt-1"
                          checked={destination === "update"}
                          onChange={() => setDestination("update")}
                        />
                        <span>
                          <span className="font-medium text-slate-800">Update current template</span>
                          {templateName ? (
                            <span className="text-slate-500"> — {templateName}</span>
                          ) : null}
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Writes these questions onto the selected template. Participant snapshots are not changed.
                          </span>
                        </span>
                      </label>
                    ) : null}
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bulk-destination"
                        className="mt-1"
                        checked={destination === "create"}
                        onChange={() => setDestination("create")}
                      />
                      <span>
                        <span className="font-medium text-slate-800">Create new named template</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Inserts a new template from this file and switches the form to it.
                        </span>
                      </span>
                    </label>
                  </div>

                  {destination === "update" ? (
                    <div className="pl-6 space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer text-sm">
                        <input
                          type="radio"
                          name="bulk-update-mode"
                          className="mt-1"
                          checked={updateMode === "merge"}
                          onChange={() => setUpdateMode("merge")}
                        />
                        <span>
                          <span className="font-medium">Merge</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Update matched wording, type, and options; add new rows; keep questions not in the file.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer text-sm">
                        <input
                          type="radio"
                          name="bulk-update-mode"
                          className="mt-1"
                          checked={updateMode === "replace"}
                          onChange={() => setUpdateMode("replace")}
                        />
                        <span>
                          <span className="font-medium">Replace</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            The file becomes the full question set. Questions not in the file are removed.
                          </span>
                        </span>
                      </label>
                      {updateMode === "replace" && missingRows.length ? (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          Replace will remove {missingRows.length} existing question
                          {missingRows.length === 1 ? "" : "s"} that are not in this file.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold text-[#004F73] mb-1">
                        New template name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && canSubmit) handleApply();
                        }}
                        placeholder="e.g. Q3 Assessment"
                        className="w-full border border-[#004F73]/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004F73]/25"
                      />
                    </div>
                  )}
                  {!analysis.can_apply ? (
                    <p className="text-xs text-amber-800">
                      Fix unmatched rows in the preview before applying.
                    </p>
                  ) : null}
                </div>
              ) : null}
              {missingRows.length ? (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    In the current set, not in the file ({missingRows.length})
                  </p>
                  <div className="max-h-32 overflow-auto border rounded-lg text-xs">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Pillar</th>
                          <th className="p-2 text-left">Question</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingRows.map((r) => (
                          <tr key={r.id} className="border-t">
                            <td className="p-2 whitespace-nowrap">{r.pillar}</td>
                            <td className="p-2">{r.text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-3 px-5 py-4 border-t bg-slate-50">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-lg bg-[#004F73] text-white font-semibold hover:opacity-90 disabled:opacity-50"
            disabled={!canSubmit}
            onClick={handleApply}
            title={
              !canSubmit
                ? destination === "create"
                  ? "Fix unmatched rows if needed, then enter a name for the new template"
                  : "Fix unmatched rows in the preview, then update the selected template"
                : destination === "create"
                  ? "Create a new template from the mapped questions"
                  : "Write these questions onto the selected template"
            }
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
